import ExcelJS from 'exceljs';
import {
  type AdoptionReportEntry,
  type BranchReportEntry,
  getTotalEmrFilled,
  getTotalExternalCompletion,
  getPendingVisits,
  getRogerVsExternalPercentage,
} from '../types/adoptionReport.types';
import {
  getPendingVisits as getVisitPendingVisits,
  getRogerVsExternalPercentage as getVisitRogerVsExternalPercentage,
} from '../types/visitCompletion.types';
import { getClinicianFullName } from '../types/clinician.types';
import { downloadBlobAsFile } from './downloadFile';

type ReportEntry = AdoptionReportEntry | BranchReportEntry;

export interface AdoptionReportExportOptions {
  /**
   * Lowercased RogerDoc username -> phone number. Omitted when the caller may
   * not read phone numbers, which drops the Phone column from the export so it
   * matches the on-screen table.
   */
  phonesByUsername?: Map<string, string>;
}

const phoneForEntry = (
  entry: ReportEntry,
  phonesByUsername: Map<string, string> | undefined
): string =>
  phonesByUsername?.get(entry.clinician.username.toLowerCase()) ?? '';

const escapeCsvValue = (value: unknown) => {
  if (value == null) return '';
  const stringValue = String(value);
  if (
    stringValue.includes(',') ||
    stringValue.includes('"') ||
    stringValue.includes('\n')
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

export const exportAdoptionReportToCSV = (
  data: ReportEntry[],
  filenameBase: string,
  includeBranch: boolean = false,
  options: AdoptionReportExportOptions = {}
) => {
  const { phonesByUsername } = options;
  const includePhone = phonesByUsername !== undefined;
  const headers = [
    'Clinician Name (first last)',
    'Title',
    ...(includePhone ? ['Phone'] : []),
    ...(includeBranch ? ['Branch'] : []),
    'Has Logged In',
    'Pending Visits',
    'Pending Visits - OASIS',
    'Pending Visits - Regular',
    'Completed in Roger',
    'Completed in Roger - OASIS',
    'Completed in Roger - Regular',
    'Completed Outside of Roger',
    'Completed Outside of Roger - OASIS',
    'Completed Outside of Roger - Regular',
    '% Completed in Roger',
    '% Completed in Roger - OASIS',
    '% Completed in Roger - Regular',
  ];

  const csvRows = data.map(entry => {
    const totalPercentage = getRogerVsExternalPercentage(entry) + '%';
    const branchName =
      includeBranch && 'branchName' in entry
        ? (entry as BranchReportEntry).branchName
        : '';

    const row = [
      escapeCsvValue(getClinicianFullName(entry.clinician)),
      escapeCsvValue(entry.clinician.title || ''),
      ...(includePhone
        ? [escapeCsvValue(phoneForEntry(entry, phonesByUsername))]
        : []),
      ...(includeBranch ? [escapeCsvValue(branchName)] : []),
      escapeCsvValue(entry.clinician.hasLoggedIn ? 'Yes' : 'No'),
      escapeCsvValue(getPendingVisits(entry)),
      escapeCsvValue(getVisitPendingVisits(entry.oasis)),
      escapeCsvValue(getVisitPendingVisits(entry.regular)),
      escapeCsvValue(getTotalEmrFilled(entry)),
      escapeCsvValue(entry.oasis.emrFilled),
      escapeCsvValue(entry.regular.emrFilled),
      escapeCsvValue(getTotalExternalCompletion(entry)),
      escapeCsvValue(entry.oasis.externalCompletion),
      escapeCsvValue(entry.regular.externalCompletion),
      escapeCsvValue(totalPercentage),
      escapeCsvValue(getVisitRogerVsExternalPercentage(entry.oasis) + '%'),
      escapeCsvValue(getVisitRogerVsExternalPercentage(entry.regular) + '%'),
    ];

    return row.join(',');
  });

  const csvContent = [headers.map(escapeCsvValue).join(','), ...csvRows].join(
    '\n'
  );

  downloadBlobAsFile(
    csvContent,
    `${filenameBase}.csv`,
    'text/csv;charset=utf-8;'
  );
};

export const exportAdoptionReportToXLSX = async (
  data: ReportEntry[],
  filenameBase: string,
  includeBranch: boolean = false,
  sheetName: string = 'Adoption Report',
  options: AdoptionReportExportOptions = {}
) => {
  const { phonesByUsername } = options;
  const includePhone = phonesByUsername !== undefined;
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);

  worksheet.columns = [
    {
      header: 'Clinician Name (first last)',
      key: 'clinicianName',
      width: 30,
    },
    { header: 'Title', key: 'title', width: 20 },
    ...(includePhone ? [{ header: 'Phone', key: 'phone', width: 18 }] : []),
    ...(includeBranch ? [{ header: 'Branch', key: 'branch', width: 30 }] : []),
    { header: 'Has Logged In', key: 'hasLoggedIn', width: 15 },
    { header: 'Pending Visits', key: 'pending', width: 18 },
    {
      header: 'Pending Visits - OASIS',
      key: 'pendingOasis',
      width: 22,
    },
    {
      header: 'Pending Visits - Regular',
      key: 'pendingRegular',
      width: 24,
    },
    { header: 'Completed in Roger', key: 'roger', width: 20 },
    {
      header: 'Completed in Roger - OASIS',
      key: 'rogerOasis',
      width: 24,
    },
    {
      header: 'Completed in Roger - Regular',
      key: 'rogerRegular',
      width: 26,
    },
    {
      header: 'Completed Outside of Roger',
      key: 'external',
      width: 26,
    },
    {
      header: 'Completed Outside of Roger - OASIS',
      key: 'externalOasis',
      width: 30,
    },
    {
      header: 'Completed Outside of Roger - Regular',
      key: 'externalRegular',
      width: 32,
    },
    {
      header: '% Completed in Roger',
      key: 'rogerPercentage',
      width: 20,
    },
    {
      header: '% Completed in Roger - OASIS',
      key: 'rogerPercentageOasis',
      width: 26,
    },
    {
      header: '% Completed in Roger - Regular',
      key: 'rogerPercentageRegular',
      width: 28,
    },
  ];

  data.forEach(entry => {
    const totalPercentage = getRogerVsExternalPercentage(entry) + '%';
    const branchName =
      includeBranch && 'branchName' in entry
        ? (entry as BranchReportEntry).branchName
        : '';

    worksheet.addRow({
      clinicianName: getClinicianFullName(entry.clinician),
      title: entry.clinician.title || '',
      ...(includePhone
        ? { phone: phoneForEntry(entry, phonesByUsername) }
        : {}),
      ...(includeBranch ? { branch: branchName } : {}),
      hasLoggedIn: entry.clinician.hasLoggedIn ? 'Yes' : 'No',
      pending: getPendingVisits(entry),
      pendingOasis: getVisitPendingVisits(entry.oasis),
      pendingRegular: getVisitPendingVisits(entry.regular),
      roger: getTotalEmrFilled(entry),
      rogerOasis: entry.oasis.emrFilled,
      rogerRegular: entry.regular.emrFilled,
      external: getTotalExternalCompletion(entry),
      externalOasis: entry.oasis.externalCompletion,
      externalRegular: entry.regular.externalCompletion,
      rogerPercentage: totalPercentage,
      rogerPercentageOasis: `${getVisitRogerVsExternalPercentage(entry.oasis)}%`,
      rogerPercentageRegular: `${getVisitRogerVsExternalPercentage(entry.regular)}%`,
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  downloadBlobAsFile(
    buffer,
    `${filenameBase}.xlsx`,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  );
};
